import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { take } from 'rxjs/operators';

import { TaskService } from '../../../../core/timeline';
import { ContactModel, ContactRelationshipsService } from '../../../../core/lists';
import { GeneralAutocompleteComponent } from '../../../partials/layout/general-autocomplete/general-autocomplete.component';
import { EntityRelationFilter } from '../../documents-management/models';
import { SharingMethod } from '../../documents-management/enums';

import { TaskAttachment } from '../models';

@Component({
  selector: 'kt-share-task-attachment',
  templateUrl: './share-task-attachment.component.html',
  styleUrls: ['./share-task-attachment.component.scss'],
})
export class ShareTaskAttachmentComponent implements OnInit {
  form: FormGroup;
  sharingMethods = Object.values(SharingMethod);
  getReceiverAutocomplete;
  @ViewChild('receiverAutocomplete') receiverAutocomplete: GeneralAutocompleteComponent;

  quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ script: 'sub' }, { script: 'super' }],
      [{ size: ['small', false, 'large', 'huge'] }],
      [{ header: [1, 2, 3, 4, 5, 6] }],
      ['clean'],
    ],
  };

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      taskId: number;
      selectedAttachment: TaskAttachment;
      attachments: TaskAttachment[];
      entitiesIds: number[];
    },
    private dialogRef: MatDialogRef<ShareTaskAttachmentComponent>,
    private formBuilder: FormBuilder,
    private taskService: TaskService,
    private contactRelationshipsService: ContactRelationshipsService,
  ) {}

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      subject: ['', Validators.required],
      attachments: [
        this.data.selectedAttachment ? [this.data.selectedAttachment] : null,
        Validators.required,
      ],
      methods: [null, Validators.required],
      body: [''],
    });
  }

  getReceiversFilter(): Partial<EntityRelationFilter> {
    const methods = this.form.value.methods;

    return {
      entityIds: this.data.entitiesIds,
      hasEmail: methods?.includes(SharingMethod.EMAIL),
      hasFax: methods?.includes(SharingMethod.FAX),
    };
  }

  handleMethodsChange(): void {
    this.getReceiverAutocomplete =
      this.contactRelationshipsService.findEntitiesRelationsAutocomplete.bind(
        this.contactRelationshipsService,
        this.getReceiversFilter(),
      );
  }

  formatReceiverOption(receiver: ContactModel): string {
    return receiver?.id
      ? `${receiver.first_name || ''} ${receiver.middle_name || ''} ${receiver.last_name || ''} ${
          receiver.last_name2 || ''
        }`.trim()
      : '';
  }

  formatNewReceiver(receiver: ContactModel): ContactModel {
    return receiver;
  }

  isInvalid(name: string): boolean {
    const field = this.form.get(name);

    return field.touched && field.invalid;
  }

  getErrorMessage(label: string): string {
    return `${label} field is required`;
  }

  close(event: Event): void {
    event.stopPropagation();

    this.dialogRef.close();
  }

  send(event: Event): void {
    event.stopPropagation();

    this.form.markAllAsTouched();
    this.receiverAutocomplete.updateValueAndValidity();

    if (this.form.invalid || this.receiverAutocomplete.isInvalid()) return;

    const formVal = this.form.value;
    const data = {
      subject: formVal.subject,
      task_attachments: formVal.attachments,
      methods: formVal.methods,
      receivers: this.receiverAutocomplete.value.map((r) => r.id),
      body: formVal.body,
    };

    this.taskService
      .shareTaskAttachments(this.data.taskId, data)
      .pipe(take(1))
      .subscribe((res) => {
        if (!res?.ok) return;

        this.dialogRef.close(true);
      });
  }
}
